import { api, apiFetch } from '@/api/client';

/** Documents per person (A1, VCA, Identiteitsbewijs, …), kept in Odoo's Documents app; see API routes/persoonsdocumenten.ts. */

export type VerplichtVoor = 'iedereen' | 'limosa' | 'niemand';
export type PersoonsdocumentStatus = 'aanwezig' | 'ontbreekt' | 'verlopen' | 'niet_nodig';

/** A type the office keeps in Odoo, with this person's state today. */
export interface PersoonsdocumentType {
  id: number;
  name: string;
  verplichtVoor: VerplichtVoor;
  required: boolean;
  status: PersoonsdocumentStatus;
  /** The document the status is about, if any. */
  documentId: number | null;
}

export interface Persoonsdocument {
  id: number;
  partnerId: number;
  typeId: number;
  /** Null for a document without a date (e.g. tagged by hand in Odoo): it counts once it has one. */
  geldigTot: string | null;
  /** In Odoo's bin: deleted for good after 30 days. */
  gearchiveerd: boolean;
  hasDocument: boolean;
  /** More than one type tag in Odoo: counts for nothing until one is removed. */
  meerdereTypes: boolean;
  uploadedBy: string | null;
  uploadedAt: string | null;
  opmerking: string | null;
}

export interface PersoonsdocumentenData {
  types: PersoonsdocumentType[];
  records: Persoonsdocument[];
  /** After how many days Odoo empties its bin; null when there are no types. */
  binDays: number | null;
}

export function fetchPersoonsdocumenten(partnerId: number): Promise<PersoonsdocumentenData> {
  return api(`/api/administratie/personen/${partnerId}/documenten`);
}

export interface PersoonsdocumentUpload {
  typeId: number;
  geldigTot: string;
  opmerking?: string;
  /** "Vervangen": this document is archived once the new one is stored. */
  vervangt?: number;
  file: File;
}

export function uploadPersoonsdocument(partnerId: number, input: PersoonsdocumentUpload): Promise<{ id: number }> {
  const fd = new FormData();
  // Text fields BEFORE the file: fields after the stream can be lost.
  fd.append('typeId', String(input.typeId));
  fd.append('geldigTot', input.geldigTot);
  if (input.opmerking) fd.append('opmerking', input.opmerking);
  if (input.vervangt) fd.append('vervangt', String(input.vervangt));
  fd.append('file', input.file);
  return api(`/api/administratie/personen/${partnerId}/documenten`, { method: 'POST', body: fd });
}

export function updatePersoonsdocument(
  id: number,
  change: { geldigTot?: string; opmerking?: string; gearchiveerd?: boolean },
): Promise<unknown> {
  return api(`/api/administratie/persoonsdocumenten/${id}`, { method: 'PATCH', body: JSON.stringify(change) });
}

/** Really delete: the file is gone, not archived. */
export function deletePersoonsdocument(id: number): Promise<unknown> {
  return api(`/api/administratie/persoonsdocumenten/${id}`, { method: 'DELETE' });
}

/** Open the file in a new tab; the route is authenticated, so it goes through a blob. */
export async function openPersoonsdocument(id: number): Promise<void> {
  // Open the tab synchronously, inside the click, or a popup blocker eats it.
  const tab = window.open('', '_blank');
  try {
    const res = await apiFetch(`/api/administratie/persoonsdocumenten/${id}/bestand`);
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    throw err;
  }
}
